import { useState } from 'react';
import { Box, Link, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import {
  COMPACT_ROW_ID_SX,
  COMPACT_ROW_LINK_SX,
  COMPACT_ROW_META_SX,
  COMPACT_ROW_NAME_SX,
  COMPACT_ROW_STATUS_LABEL_SX,
  COMPACT_ROW_SX,
  CompactRowList,
  MUTED_ICON_OPACITY,
  MUTED_TEXT_COLOR,
  RowPipe,
} from 'src/components/CompactRowList';
import Iconify from 'src/components/Iconify';
import InfoAlert from 'src/components/InfoAlert';
import NoteTooltip from 'src/components/NoteTooltip';
import TableAppImage from 'src/components/Table/TableAppImage';
import { formatDate } from 'src/utils/formatTime';
import { getCustomerEnvironmentApplicationSourceLabel } from 'src/utils/enums/customerEnvironmentApplicationSource';
import {
  getCustomerEnvironmentApplicationStatusLabel,
  isCustomerEnvironmentApplicationEnabled,
} from 'src/utils/enums/customerEnvironmentApplicationStatus';
import { useWin32AppFileSets } from 'src/hooks/wdac';
import FileSetTemplateLinks from './FileSetTemplateLinks';
import Win32FileSetModal from './Win32FileSetModal';

const SECTION_HELP =
  "Enabled imports have their files included in the security catalog, which spans the whole app. Which imports are enabled is chosen when you build the app's trusted artifacts.";

export const formatFileCountMeta = (fileCount, date) =>
  `${(fileCount || 0).toLocaleString()} ${fileCount === 1 ? 'file' : 'files'}${
    date ? ` • ${formatDate(date, 'yyyy-MM-dd')}` : ''
  }`;

// One thin row per import. The caller supplies what sits at its end: the card a status label, the
// status form a checkbox. Where the row's own click does something else, onHashClick makes the hash
// the way in to the import's details instead.
export const FileSetImportRow = ({
  row,
  onClick,
  onHashClick,
  dimOpacity = 1,
  muted = false,
  showTemplates,
  trailing,
}) => {
  const sourceLabel = getCustomerEnvironmentApplicationSourceLabel(row.originalSource);
  const mutedColor = muted ? { color: MUTED_TEXT_COLOR } : null;

  return (
    <Stack
      direction="row"
      gap={1.25}
      alignItems="center"
      onClick={onClick}
      sx={{ ...COMPACT_ROW_SX, opacity: dimOpacity }}>
      <Box display="inline-flex" sx={{ flexShrink: 0, opacity: muted ? MUTED_ICON_OPACITY : 1 }}>
        <TableAppImage width={20} height={20} name={sourceLabel} imgSrc={row.iconUrl} />
      </Box>
      <Typography sx={{ ...COMPACT_ROW_NAME_SX, ...mutedColor }}>
        {sourceLabel || 'File Set Import'}
      </Typography>
      <Tooltip
        title={row.fileSetHash || ''}
        arrow
        componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 420 } } }}>
        <Typography
          onClick={
            onHashClick
              ? event => {
                  event.stopPropagation();
                  onHashClick();
                }
              : undefined
          }
          sx={{
            ...COMPACT_ROW_ID_SX,
            ...mutedColor,
            ...(onHashClick ? COMPACT_ROW_LINK_SX : null),
          }}>
          {row.fileSetHash || '—'}
        </Typography>
      </Tooltip>
      <Typography sx={{ ...COMPACT_ROW_META_SX, ...mutedColor }}>
        {formatFileCountMeta(row.fileCount, row.createdAt)}
      </Typography>
      {showTemplates ? (
        <Box sx={{ flexShrink: 0 }}>
          <FileSetTemplateLinks fileSet={row} emptyLabel="No templates" />
        </Box>
      ) : null}
      <NoteTooltip note={row.notes} />
      {trailing}
    </Stack>
  );
};

// Which of the imports a trusted app is made of. They are switched on and off where the app's
// artifacts are built, so a row's status label opens that step rather than changing anything itself.
const Win32FileSetImports = ({ globalApplicationVersionId, onEditImports }) => {
  const [openFileSetId, setOpenFileSetId] = useState(null);

  const { data: fileSets, isLoading } = useWin32AppFileSets({ globalApplicationVersionId });
  const rows = fileSets || [];

  return (
    <Stack gap={0.5}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem', fontWeight: 600 }}>
          File Set Imports
        </Typography>
        <Tooltip
          title={SECTION_HELP}
          arrow
          componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 380 } } }}>
          <Box component="span" display="inline-flex">
            <Iconify icon="material-symbols:info-outline" width={14} height={14} color="#9CA3AF" />
          </Box>
        </Tooltip>
      </Stack>

      {isLoading ? (
        <Stack gap={0.5}>
          <Skeleton variant="rounded" height={30} sx={{ bgcolor: '#4B4B4B' }} />
          <Skeleton variant="rounded" height={30} sx={{ bgcolor: '#4B4B4B' }} />
        </Stack>
      ) : rows.length === 0 ? (
        <InfoAlert sx={{ py: 0.25 }}>
          <Typography variant="caption">
            No file set imports have been identified for this application in this environment yet.
            Each import defines which files make up the app.
          </Typography>
        </InfoAlert>
      ) : (
        <CompactRowList>
          {rows.map(row => {
            const enabled = isCustomerEnvironmentApplicationEnabled(row.status);

            return (
              <FileSetImportRow
                key={row.customerEnvironmentApplicationId}
                row={row}
                onClick={() => setOpenFileSetId(row.fileSetId)}
                dimOpacity={enabled ? 1 : 0.3}
                showTemplates
                trailing={
                  <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
                    <RowPipe />
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={event => {
                        event.stopPropagation();
                        onEditImports?.();
                      }}
                      sx={{
                        ...COMPACT_ROW_STATUS_LABEL_SX,
                        color: enabled ? '#87C8E0' : '#9CA3AF',
                      }}>
                      {getCustomerEnvironmentApplicationStatusLabel(row.status)}
                    </Link>
                  </Stack>
                }
              />
            );
          })}
        </CompactRowList>
      )}

      <Win32FileSetModal
        open={openFileSetId != null}
        onClose={() => setOpenFileSetId(null)}
        globalApplicationVersionId={globalApplicationVersionId}
        fileSetId={openFileSetId}
      />
    </Stack>
  );
};

export default Win32FileSetImports;
